import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { User, Mail, Phone, MapPin, Heart, History, Camera } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import ProfilePhotoUpload from './ui/ProfilePhotoUpload';
import { useTranslation } from 'react-i18next';

const StudentProfileManager = () => {
  useUiLanguage();
  const { user, profile, updateProfile } = useAuth();
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    dateOfBirth: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    medicalConditions: '',
    currentMedications: '',
    allergies: '',
    trainingGoals: ''
  });

  useEffect(() => {
    if (profile) {
      setProfileData(prev => ({
        ...prev,
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        postalCode: profile.postal_code || '',
        dateOfBirth: (profile as any).date_of_birth || ''
      }));
      setIsInitialLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (!user?.id) return;

      try {
        // Fetch emergency contact
        const { data: emergencyData } = await supabase
          .from('emergency_contacts')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (emergencyData) {
          setProfileData(prev => ({
            ...prev,
            emergencyContactName: emergencyData.name || '',
            emergencyContactPhone: emergencyData.phone || '',
            emergencyContactRelationship: emergencyData.relationship || ''
          }));
        }

        // Fetch medical info
        const { data: medicalData } = await supabase
          .from('medical_info')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (medicalData) {
          setProfileData(prev => ({
            ...prev,
            medicalConditions: medicalData.medical_conditions || '',
            currentMedications: medicalData.current_medications || '',
            allergies: medicalData.allergies || ''
          }));
        }

        // Fetch martial arts background
        const { data: backgroundData } = await supabase
          .from('martial_arts_background')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (backgroundData) {
          setProfileData(prev => ({
            ...prev,
            trainingGoals: backgroundData.training_goals || ''
          }));
        }

      } catch (error) {
        console.error('Error fetching additional profile data:', error);
      }
    };

    fetchAdditionalData();
  }, [user?.id]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.id) {
      toast({
        title: t("common:messages.error"),
        description: t("common:messages.invalidEmail"),
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Update main profile including date of birth
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          email: profileData.email,
          phone: profileData.phone,
          address: profileData.address,
          city: profileData.city,
          postal_code: profileData.postalCode,
          date_of_birth: profileData.dateOfBirth || null
        })
        .eq('id', user.id);

      if (profileUpdateError) {
        throw new Error(profileUpdateError.message);
      }

      // Update emergency contact
      const { error: emergencyError } = await supabase
        .from('emergency_contacts')
        .upsert({
          user_id: user.id,
          name: profileData.emergencyContactName,
          phone: profileData.emergencyContactPhone,
          relationship: profileData.emergencyContactRelationship
        });

      if (emergencyError) {
        console.error('Error updating emergency contact:', emergencyError);
      }

      // Update medical info
      const { error: medicalError } = await supabase
        .from('medical_info')
        .upsert({
          user_id: user.id,
          medical_conditions: profileData.medicalConditions,
          current_medications: profileData.currentMedications,
          allergies: profileData.allergies
        });

      if (medicalError) {
        console.error('Error updating medical info:', medicalError);
      }

      // Update martial arts background
      const { error: backgroundError } = await supabase
        .from('martial_arts_background')
        .upsert({
          user_id: user.id,
          training_goals: profileData.trainingGoals
        });

      if (backgroundError) {
        console.error('Error updating martial arts background:', backgroundError);
      }

      // Refresh the profile in AuthContext to show updated data
      await updateProfile({
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        address: profileData.address,
        city: profileData.city,
        postal_code: profileData.postalCode
      });

      toast({
        title: t("common:messages.success"),
        description: uiText("Profile updated successfully")
      });

    } catch (error) {
      console.error('Profile update error:', error);
      toast({
        title: t("common:messages.error"),
        description: t("common:messages.error"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpdate = async (photoUrl: string | null) => {
    if (!profile?.id) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ profile_photo_url: photoUrl })
        .eq('id', profile.id);

      if (error) throw error;

      // Update profile through the updateProfile function from useAuth
      await updateProfile({ profile_photo_url: photoUrl });

      toast({
        title: uiText("Success"),
        description: uiText("Profile photo updated successfully")
      });
    } catch (error) {
      console.error('Error updating profile photo:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update profile photo"),
        variant: "destructive"
      });
    }
  };

  // Show loading state while profile is being loaded
  if (isInitialLoading || !profile) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t("common:navigation.profile")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-900 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">{uiText("Loading profile...")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t("common:navigation.profile")}
          </CardTitle>
          <CardDescription>
            {t("common:students.updateInformation")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Profile Photo Section */}
          <div className="flex justify-center mb-6 p-4 border-b">
            <div className="text-center">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t("common:navigation.profile")} {' '}{uiText("Photo")}</h3>
              <div className="flex flex-col items-center gap-4">
                {profile?.profile_photo_url && (
                  <img
                    src={profile.profile_photo_url}
                    alt={uiText("Profile")}
                    className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                  />
                )}
                <ProfilePhotoUpload
                  currentPhotoUrl={profile?.profile_photo_url}
                  onPhotoUpdate={handlePhotoUpdate}
                  className="standalone flex gap-2"
                />
              </div>
            </div>
          </div>

          <form onSubmit={handleProfileUpdate} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <User className="h-4 w-4" />
                {t("common:students.updateInformation")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">{t("common:students.firstName")}</Label>
                  <Input
                    id="firstName"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">{t("common:students.lastName")}</Label>
                  <Input
                    id="lastName"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t("common:labels.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="dateOfBirth">{t("common:labels.dateOfBirth")}</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={profileData.dateOfBirth}
                    onChange={(e) => setProfileData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">{t("common:labels.phone")}</Label>
                  <Input
                    id="phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="address">{t("common:labels.address")}</Label>
                  <Input
                    id="address"
                    value={profileData.address}
                    onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="city">{t("common:labels.city")}</Label>
                  <Input
                    id="city"
                    value={profileData.city}
                    onChange={(e) => setProfileData(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="postalCode">{t("common:labels.postalCode")}</Label>
                  <Input
                    id="postalCode"
                    value={profileData.postalCode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, postalCode: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Phone className="h-4 w-4" />
                {uiText("Emergency Contact")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="emergencyContactName">{uiText("Contact Name")}</Label>
                  <Input
                    id="emergencyContactName"
                    value={profileData.emergencyContactName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, emergencyContactName: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyContactPhone">{uiText("Contact Phone")}</Label>
                  <Input
                    id="emergencyContactPhone"
                    value={profileData.emergencyContactPhone}
                    onChange={(e) => setProfileData(prev => ({ ...prev, emergencyContactPhone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyContactRelationship">{uiText("Relationship")}</Label>
                  <Input
                    id="emergencyContactRelationship"
                    value={profileData.emergencyContactRelationship}
                    onChange={(e) => setProfileData(prev => ({ ...prev, emergencyContactRelationship: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Medical Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Heart className="h-4 w-4" />
                {uiText("Medical Information")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="medicalConditions">{uiText("Medical Conditions")}</Label>
                  <Textarea
                    id="medicalConditions"
                    value={profileData.medicalConditions}
                    onChange={(e) => setProfileData(prev => ({ ...prev, medicalConditions: e.target.value }))}
                    placeholder={uiText("List any medical conditions")}
                  />
                </div>
                <div>
                  <Label htmlFor="currentMedications">{uiText("Current Medications")}</Label>
                  <Textarea
                    id="currentMedications"
                    value={profileData.currentMedications}
                    onChange={(e) => setProfileData(prev => ({ ...prev, currentMedications: e.target.value }))}
                    placeholder={uiText("List any current medications")}
                  />
                </div>
                <div>
                  <Label htmlFor="allergies">{uiText("Allergies")}</Label>
                  <Input
                    id="allergies"
                    value={profileData.allergies}
                    onChange={(e) => setProfileData(prev => ({ ...prev, allergies: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Training Goals */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <History className="h-4 w-4" />
                {uiText("Training Goals")}</h3>
              <div>
                <Label htmlFor="trainingGoals">{uiText("My Training Goals")}</Label>
                <Textarea
                  id="trainingGoals"
                  value={profileData.trainingGoals}
                  onChange={(e) => setProfileData(prev => ({ ...prev, trainingGoals: e.target.value }))}
                  placeholder={uiText("What do you hope to achieve through your karate training?")}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="bg-red-900 hover:bg-red-800"
              disabled={isLoading}
            >
              <User className="h-4 w-4 mr-2" />
              {isLoading ? t("common:buttons.processing") : t("common:buttons.update")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentProfileManager;